import { takeLatest, takeEvery, call, put, select, all, take } from 'redux-saga/effects';
import delay from '@redux-saga/delay-p';

import { CKForm, enums } from 'console-kit';

import RefData from 'ref-data';

import { minDurationEffect } from '../../common/redux';
import AppActions from '../../Integrations/redux/actions';
import ConsoleAppActions from '../../App/redux/app-actions';
import { Experience } from '../index';
import IntegrationActions from './integration-actions';
import IntegrationApi from './integration-api';
import IntegrationSelectors from './integration-selectors';
import IntegrationEnums from './integration-enums';
import dataSourceApi from './data-source-api';

function* fetchIntegrationsHandler({ payload: { containerRef, classRef, typeRef } }) {
  try {
    const _containerRef = yield select(IntegrationSelectors.listIntegrationsContainerRef, containerRef);
    const _classRef = yield select(IntegrationSelectors.listIntegrationsClassRef, classRef);

    const _integrations = yield call(IntegrationApi.fetch, {
      containerRef: _containerRef,
      classRef:     _classRef,
      typeRef:      typeRef,
    });
    yield put(IntegrationActions.fetchIntegrationsSuccess(_integrations));
  } catch (error) {
    yield put(IntegrationActions.fetchIntegrationsFailure(error));
  }
}

function* fetchIntegrationTemplatesHandler({ payload: { containerRef, classRef } }) {
  const experience = yield select(Experience.Selectors.experience);
  const validFor = _.get(experience, ['formatType', 'id'], null);

  const _templates = yield call(IntegrationApi.fetchTemplates, {
    containerRef,
    classRef,
    validFor,
  });

  if (_templates.error) yield put(IntegrationActions.fetchIntegrationTemplatesFailure(_templates.error));
  else yield put(IntegrationActions.fetchIntegrationTemplatesSuccess(_templates));
}

function* createFromTemplateHandler({ payload: { containerRef, templateId, context, wizardFields } }) {
  try {
    const { integration } = yield all({
      // using delay here as a minimum duration for the API call to ensure the UI state transitions properly
      pause:       delay(500),
      integration: call(IntegrationApi.createFromTemplate, {
        containerRef,
        templateId,
        context,
        wizardFields,
      }),
    });

    yield put(IntegrationActions.createFromTemplateSuccess(integration));
  } catch (error) {
    yield put(IntegrationActions.createFromTemplateFailure(error));
  }
}

function* enableIntegrationHandler({ payload: { integrationId } }) {
  try {
    yield call(IntegrationApi.enableIntegration, { integrationId });
    yield put(IntegrationActions.enableIntegrationSuccess());
  } catch (error) {
    yield put(IntegrationActions.enableIntegrationFailure(error, integrationId));
  }
}

function* disableIntegrationHandler({ payload: { integrationId } }) {
  try {
    yield call(IntegrationApi.disableIntegration, { integrationId });
    yield put(IntegrationActions.disableIntegrationSuccess());
  } catch (error) {
    yield put(IntegrationActions.disableIntegrationFailure(error, integrationId));
  }
}

function* deleteIntegrationHandler({ payload: { integrationId } }) {
  try {
    yield call(IntegrationApi.deleteIntegration, { integrationId });
    yield put(IntegrationActions.deleteIntegrationSuccess(integrationId));
  } catch (error) {
    yield put(IntegrationActions.deleteIntegrationFailure(error));
  }
}

function* updateIntegrationHandler({ payload: { integration } }) {
  try {
    const _integration = yield minDurationEffect(call(IntegrationApi.updateIntegration, { integration }));
    yield put(IntegrationActions.updateIntegrationSuccess(_integration));
  } catch (error) {
    yield put(IntegrationActions.updateIntegrationFailure(error));
  }
}

function* updateAndActivateIntegrationHandler({ payload: { integration } }) {
  try {
    const _integration = yield minDurationEffect(
      call(IntegrationApi.updateIntegration, {
        integration,
        status: RefData.Enum.STATUS.INTEGRATION.ACTIVE,
      }),
      300
    );
    yield put(IntegrationActions.updateAndActivateIntegrationSuccess(_integration));
  } catch (error) {
    yield put(IntegrationActions.updateAndActivateIntegrationFailure(error));
  }
}

function* requestDisableIntegrationHandler({ payload: { integrationId, classRef } }) {
  if (classRef === RefData.Enum.CLASS.INTEGRATION.ACCOUNT) {
    yield put(AppActions.showDisableModal(integrationId));
  } else {
    yield put(IntegrationActions.disableIntegration(integrationId));
  }
}

/* WIZARD INTEGRATIONS SAGAS */
function* validateFormAndGoNextHandler({ payload: { formId } }) {
  yield put(CKForm.Actions.validateForm(formId));
  const isFormValid = yield select(CKForm.Selectors.isFormValid, formId);
  yield put(IntegrationActions.toggleAllowSwitchToNextStep(isFormValid));
}

function* fetchDataSourceHandler({ payload: { fieldId, url } }) {
  const { fields, apiUrl, campaignId } = yield select(IntegrationSelectors.wizardIntegration);

  // for example `/api/external/emma/groups?apiKey={apiKey}` => `/api/external/emma/groups?apiKey=<real value from the store>`
  let preparedUrl = url.replace(/{(\w*)}/g, (found) => {
    const field = found.slice(1, -1);
    return encodeURIComponent(fields[field]);
  });

  if (apiUrl) {
    preparedUrl += `${preparedUrl.indexOf('?') !== -1 ? '&' : '?'}apiUrl=${encodeURIComponent(apiUrl)}`;
  }

  if (campaignId) {
    preparedUrl += `${preparedUrl.indexOf('?') !== -1 ? '&' : '?'}campaignId=${campaignId}`;
  }

  const dataSource = yield call(dataSourceApi.fetchDataSource, preparedUrl);

  if (dataSource.error) {
    if (fieldId) {
      yield put(IntegrationActions.fetchDataSourceFailure(dataSource.error, fieldId));
    } else {
      yield put(IntegrationActions.fetchMappingDataFailure(dataSource.error));
    }
  } else {
    // field id is used for form data source, if we don't have it -> we request available mapping fields
    if (fieldId) {
      yield put(IntegrationActions.fetchDataSourceSuccess(fieldId, dataSource));
    } else {
      yield put(IntegrationActions.fetchMappingDataSuccess(dataSource));
    }
  }
}

function* updateWizardMappingFieldHandler({ payload: { idx, fieldName, apiRequired } }) {
  // if it's an integration side of mapping (right side), check the apiRequired field
  // and set it to the mapping object, if needed
  if (fieldName === IntegrationEnums.integrationWizardMappingDirections.TO && apiRequired) {
    yield put(IntegrationActions.updateWizardMappingRequiredField(idx));
  }
}

/**
 * Show an error, if there are no available credentials, and close the wizard
 */
function* showWizardCredentialsErrorHandler() {
  yield put(IntegrationActions.clearWizardData());
  yield put(
    ConsoleAppActions.setAlertMessage({
      messageType: enums.messageTypes.ERROR,
      header:      'An error occured while trying to get the credentials for this type of integration',
      byline:      'Please check if you have at least one credentials set for this type of integration',
    })
  );
}

/**
 * Fetch the credentials used in the selfserve integrations and displayed in the account settings
 */
function* fetchIntegrationCredentialsHandler() {
  const _credentials = yield call(IntegrationApi.fetchCredentials);
  if (_credentials.error) yield put(IntegrationActions.fetchIntegrationCredentialsFailure(_credentials.error));
  else yield put(IntegrationActions.fetchIntegrationCredentialsSuccess(_credentials));
}

/**
 * Create/update the integration credentials
 */
function* saveIntegrationCredentialsHandler({ payload: { credentialsFields, isCreating } }) {
  const _credentials = yield minDurationEffect(
    call(isCreating ? IntegrationApi.createCredentials : IntegrationApi.updateCredentials, credentialsFields)
  );
  if (_credentials.error) yield put(IntegrationActions.saveIntegrationCredentialsFailure(_credentials.error));
  else yield put(IntegrationActions.saveIntegrationCredentialsSuccess(_credentials));
}

function* deleteIntegrationCredentialsHandler({ payload: { credentialsId } }) {
  const response = yield call(IntegrationApi.deleteCredentials, { credentialsId });
  if (response && response.error) yield put(IntegrationActions.deleteIntegrationCredentialsFailure(response.error));
  else yield put(IntegrationActions.deleteIntegrationCredentialsSuccess(credentialsId));
}

function* integrationSagas() {
  yield all([takeLatest(IntegrationActions.fetchIntegrations.getType(), fetchIntegrationsHandler)]);
  yield all([takeLatest(IntegrationActions.fetchIntegrationTemplates.getType(), fetchIntegrationTemplatesHandler)]);
  yield all([takeLatest(IntegrationActions.createFromTemplate.getType(), createFromTemplateHandler)]);

  yield all([takeLatest(IntegrationActions.enableIntegration.getType(), enableIntegrationHandler)]);
  yield all([takeLatest(IntegrationActions.disableIntegration.getType(), disableIntegrationHandler)]);
  yield all([takeLatest(IntegrationActions.requestDisableIntegration.getType(), requestDisableIntegrationHandler)]);

  yield all([takeLatest(IntegrationActions.deleteIntegration.getType(), deleteIntegrationHandler)]);
  yield all([takeLatest(IntegrationActions.updateIntegration.getType(), updateIntegrationHandler)]);
  yield all([
    takeLatest(IntegrationActions.updateAndActivateIntegration.getType(), updateAndActivateIntegrationHandler),
  ]);

  yield all([
    takeLatest(IntegrationActions.validateFormAndGoNext.getType(), validateFormAndGoNextHandler),
    takeEvery(IntegrationActions.fetchDataSource.getType(), fetchDataSourceHandler),
    takeLatest(IntegrationActions.fetchMappingData.getType(), fetchDataSourceHandler),
    takeLatest(IntegrationActions.updateWizardMappingField.getType(), updateWizardMappingFieldHandler),

    takeLatest(IntegrationActions.showWizardCredentialsError.getType(), showWizardCredentialsErrorHandler),
    takeLatest(IntegrationActions.fetchIntegrationCredentials.getType(), fetchIntegrationCredentialsHandler),
    takeLatest(IntegrationActions.saveIntegrationCredentials.getType(), saveIntegrationCredentialsHandler),
    takeLatest(IntegrationActions.deleteIntegrationCredentials.getType(), deleteIntegrationCredentialsHandler),
  ]);
}

export const _testing = {
  fetchIntegrationsHandler,
  updateAndActivateIntegrationHandler,
  updateIntegrationHandler,
};

export default integrationSagas;
